import { useContext, type KeyboardEvent } from "react";
import GppMaybeOutlined from "@mui/icons-material/GppMaybeOutlined";
import Info from "@mui/icons-material/Info";
import { Badge, Box, Tooltip, Typography, useTheme } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

import { dayjs, NhsNumberSchema } from "@thalamos/common";
import type {
  Patient,
  PatientStateWithContext,
  PatientTimelineResponse,
} from "@thalamos/aspire-common";
import {
  getActiveMhaStatus,
  MhaStatus,
  selectSection136BannerStates,
} from "@thalamos/aspire-common";

import { LoggedInUserContext } from "../../../Contexts.js";
import { HorizontalLine } from "../../form/HorizontalLine.js";
import { VerticalLine } from "../../form/VerticalLine.js";
import {
  infoBoxMhaStatusTitle,
  InfoBox as MhaStatusInfoBox,
} from "./PatientStateDisplay.js";
import { Banner, BannerList } from "~/components/design-system/index.js";
import { TooltipInfoBox } from "~/components/design-system/TooltipInfoBox.js";
import { formatMhaStatus } from "~/components/util.js";
import { useMhaStatus } from "~/hooks/apiCalls.js";

/*
 * Leaf-level building blocks for the patient banner.
 *
 * These presentational primitives plus the compacted banner are shared by the
 * full PatientBanner (which additionally wires in page-level merge widgets) and
 * by the patient-merge pages. They live here, separate from PatientBanner.tsx,
 * so the merge pages can import CompactedPatientBanner without pulling in the
 * full banner's dependency on PatientAutoMergeChecker / PatientUndoMergeDialog
 * (which would create a circular dependency). Keep this module page-free.
 */

export interface PatientBannerProps {
  patient: Patient;

  patientTimeline?: PatientTimelineResponse;
  reloadPatientTimeline?: () => void;
  nhsNumber?: string;

  disableSticky?: boolean;
  isHorizontalLineHidden?: boolean;
  isHidden?: boolean;
  showUnknownMhaStatus?: boolean;
  // Read-only unmerge preview: fetch the MHA status treating these patients as
  // merge-tree roots, i.e. what the status would be after an unmerge.
  patientIdsToTreatAsRootPatients?: string[];
  // Force the MHA status info box to display this status instead of the fetched
  // one (e.g. the unmerge dialog showing the "Incomplete" a restored patient
  // will be reset to). Takes precedence over the fetched status.
  overrideMhaStatus?: MhaStatus;
  disableAutoMergeCheck?: boolean;
  onReconcileClick?: () => void;
}

export interface PatientBannerCompactedProps extends PatientBannerProps {
  showNhsLabel: boolean;
  patientMhaStatusEnabled: boolean;
  mhaStatus:
    | {
        states: PatientStateWithContext[];
      }
    | undefined;
}

export type MainTextStylesObject = {
  color: string;
  fontSize: string;
};

type BannerContainerProps = {
  issticky?: string;
  ismobilemode?: string;
  theme?: Theme;
  restricted?: boolean;
  deceased?: boolean;
};

const BannerContainer = styled("div", {
  shouldForwardProp: (prop) =>
    !["issticky", "ismobilemode", "restricted", "deceased"].includes(
      String(prop),
    ),
})<BannerContainerProps>(
  ({ issticky, ismobilemode, restricted, deceased, theme }) => ({
    flexDirection: "column",
    backgroundColor: theme.palette.secondary.light,
    borderRadius: theme.spacing(0.8),
    ...(restricted && { border: "1px solid red" }),
    ...(deceased && { border: "1px solid black" }),
    padding: issticky && ismobilemode ? theme.spacing(1.5) : theme.spacing(2.5),
  }),
);

const getSmallTextStyles = (theme: Theme) => ({
  color: "primary.hint",
  fontSize: theme.spacing(1.375),
});

const compactTextStyles = (theme: Theme) => ({
  color: "text.primary",
  fontSize: theme.spacing(1.625),
});

export const shouldShowNhsLabel = (
  nhsNumberEnabled: boolean,
  nhsNumber: string | undefined,
) => {
  return !!(nhsNumberEnabled || (!nhsNumberEnabled && nhsNumber));
};

export function Spacer() {
  const theme = useTheme();
  return (
    <Box sx={{ marginLeft: "0.7em", marginRight: "0.7em", height: "100%" }}>
      <VerticalLine color={theme.palette.text.disabled} height="100%" />
    </Box>
  );
}

export function CompactedPatientBanner(props: PatientBannerProps) {
  const userContext = useContext(LoggedInUserContext);
  const user = userContext?.user;
  const nhsNumberEnabled =
    user?.sessionOrganisationConfiguration?.nhsNumberEnabled ?? false;

  const patientMhaStatusEnabled =
    user?.sessionOrganisationConfiguration?.patientStateEnabled ?? false;

  const showNhsLabel = shouldShowNhsLabel(nhsNumberEnabled, props.nhsNumber);

  const { mhaStatus } = useMhaStatus({
    patientId: props.patient.id,
    patientIdsToTreatAsRootPatients: props.patientIdsToTreatAsRootPatients,
  });

  return (
    <CompactedPatientBannerInner
      {...props}
      showNhsLabel={showNhsLabel}
      patientMhaStatusEnabled={patientMhaStatusEnabled}
      mhaStatus={mhaStatus}
    />
  );
}

export function CompactedPatientBannerInner(
  props: PatientBannerCompactedProps,
) {
  const theme = useTheme();

  const { t } = useTranslation();

  const mainTextStyles = {
    color: "text.primary",
    fontSize: theme.spacing(1.5),
  };

  const patientAge = dayjs().diff(dayjs(props.patient.dateOfBirth), "year");

  return (
    <Box data-testid="compacted-patient-banner">
      <BannerContainer sx={{ minHeight: "170px" }}>
        <Box
          sx={{
            display: "flex",
          }}
        >
          <Box>
            <RenderPatientName
              deceased={["formal", "informal"].includes(
                props.patient.deathNotificationStatus ?? "",
              )}
              restricted={props.patient.restricted}
              familyName={props.patient.name.family}
              givenName={props.patient.name.given}
              styleType={mainTextStyles}
              fontSize={12}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  width: "100%",
                }}
              >
                {props.showNhsLabel && (
                  <>
                    <Spacer />
                    <RenderNhsNumber nhsNumber={props.nhsNumber} />
                  </>
                )}

                <Spacer />

                <RenderDateOfBirth
                  dateOfBirth={
                    props.patient.dateOfBirth
                      ? dayjs(props.patient.dateOfBirth).format("DD MMM YYYY")
                      : t("layout.patientBanner.unknown")
                  }
                  styleType={compactTextStyles(theme)}
                />

                <Spacer />
                <RenderPatientAge
                  patientAge={
                    patientAge ? patientAge : t("layout.patientBanner.unknown")
                  }
                  styleType={mainTextStyles}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ my: "0.5em" }}>
          <HorizontalLine noMargin={true} />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <RenderPatientAddress
            address={props.patient.address.address}
            styleType={mainTextStyles}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  width: "100%",
                  marginTop: "0.5em",
                }}
              >
                <Spacer />
                <RenderPostCode
                  postalCode={props.patient.address.postalCode}
                  styleType={mainTextStyles}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </BannerContainer>
      {props.patientMhaStatusEnabled && (
        <Box sx={{ my: theme.spacing(2), ml: theme.spacing(2) }}>
          <RenderMhaStatusInfoBox
            mhaStatus={props.mhaStatus}
            showUnknownMhaStatus={props.showUnknownMhaStatus}
            overrideMhaStatus={props.overrideMhaStatus}
          />
        </Box>
      )}
    </Box>
  );
}

function RenderPatientAddress({
  address,
  styleType,
}: {
  address: string;
  styleType: MainTextStylesObject;
}) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography sx={getSmallTextStyles}>
        {t("layout.patientBanner.address")}
      </Typography>
      <Typography sx={{ ...styleType }}>{address}</Typography>
    </Box>
  );
}

function RenderPatientAge({
  patientAge,
  styleType,
}: {
  patientAge: number | string;
  styleType: MainTextStylesObject;
}) {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography sx={getSmallTextStyles}>
        {t("layout.patientBanner.age")}
      </Typography>
      <Typography sx={{ ...styleType }}>{patientAge}</Typography>
    </Box>
  );
}

function RenderDateOfBirth({
  dateOfBirth,
  styleType,
}: {
  dateOfBirth: string;
  styleType: MainTextStylesObject;
}) {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography data-testid="dob-label" sx={getSmallTextStyles}>
        {t("layout.patientBanner.dateOfBirth")}
      </Typography>
      <Typography data-testid="dob" sx={{ ...styleType }}>
        {dateOfBirth}
      </Typography>
    </Box>
  );
}

function RenderPostCode({
  postalCode,
  styleType,
}: {
  postalCode: string;
  styleType: MainTextStylesObject;
}) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography data-testid="postcode-label" sx={getSmallTextStyles}>
        {t("layout.patientBanner.postcode")}
      </Typography>
      <Typography data-testid="postcode" sx={{ ...styleType }}>
        {postalCode ? postalCode : t("layout.patientBanner.unknown")}
      </Typography>
    </Box>
  );
}

function RenderGpPractice({
  odsCode,
  organisationName,
  loading,
  styleType,
}: {
  odsCode: string;
  organisationName: string | null;
  loading: boolean;
  styleType: MainTextStylesObject;
}) {
  const { t } = useTranslation();

  const displayValue = loading ? odsCode : (organisationName ?? odsCode);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography data-testid="gp-practice-label" sx={getSmallTextStyles}>
        {t("layout.patientBanner.gpPractice")}
      </Typography>
      <Typography data-testid="gp-practice" sx={{ ...styleType }}>
        {displayValue}
      </Typography>
    </Box>
  );
}

function RenderPatientName({
  restricted,
  deceased,
  familyName,
  givenName,
  styleType,
  fontSize,
}: {
  deceased: boolean;
  restricted: boolean;
  familyName: string;
  givenName: string;
  styleType: MainTextStylesObject;
  fontSize: number;
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <>
      <Box
        sx={{
          fontSize: theme.spacing(1.55),
          color: "primary.hint",
          marginBottom: "0.2em",
        }}
      >
        <Typography sx={{ ...getSmallTextStyles(theme) }}>
          {t("layout.patientBanner.name")}
        </Typography>
      </Box>
      <Typography
        data-testid="patient-name"
        sx={{
          ...styleType,
          fontWeight: 600,
          fontSize,
          display: "flex",
          justifyContent: "center",
          gap: "0.25em",
        }}
      >
        {familyName}, {givenName}{" "}
        {deceased && (
          <TooltipInfoBox
            title={
              "This patient has been marked as deceased in NHS national systems"
            }
            icon={<Info sx={{ fontSize: "1.5rem", ml: 1 }} />}
          />
        )}
        {restricted && (
          <TooltipInfoBox
            title={
              "This patient is marked as restricted in NHS national systems"
            }
            icon={
              <GppMaybeOutlined
                sx={{ color: "red", fontSize: "1.5rem", ml: 1 }}
              />
            }
          />
        )}
      </Typography>
    </>
  );
}

function RenderNhsNumber({
  nhsNumber,
  onReconcileClick,
}: {
  nhsNumber?: string;
  onReconcileClick?: () => void;
}) {
  const theme = useTheme();
  const { t } = useTranslation();

  const parsedNhsNumber = NhsNumberSchema.catch(t("common.unknown")).parse(
    nhsNumber,
  );
  const isUnknown = parsedNhsNumber === t("common.unknown");
  const isClickable = isUnknown && onReconcileClick;

  const handleClick = () => {
    onReconcileClick?.();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onReconcileClick?.();
    }
  };

  const nhsNumberContent = (
    <Typography
      data-testid="nhs-number"
      sx={{
        ...compactTextStyles(theme),
        ...(isClickable && {
          cursor: "pointer",
          textDecoration: "underline",
          "&:hover": {
            color: "primary.main",
          },
        }),
      }}
      onClick={isClickable ? handleClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={
        isClickable ? t("layout.patientBanner.reconcileTooltip") : undefined
      }
    >
      {parsedNhsNumber}
    </Typography>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        data-testid="nhs-number-label"
        sx={{ ...getSmallTextStyles(theme) }}
      >
        {t("layout.patientBanner.nhsNumber")}
      </Typography>

      {isClickable ? (
        <Tooltip
          title={t("layout.patientBanner.reconcileTooltip")}
          arrow
          placement="bottom"
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Badge
              color="error"
              variant="dot"
              sx={{
                "& .MuiBadge-badge": {
                  position: "relative",
                  transform: "none",
                },
              }}
            />
            {nhsNumberContent}
          </Box>
        </Tooltip>
      ) : (
        nhsNumberContent
      )}
    </Box>
  );
}

function mhaStatusSubtitle(state: PatientStateWithContext): string {
  return infoBoxMhaStatusTitle(
    state,
    getActiveMhaStatus(state.state!, dayjs().toISOString()),
  );
}

function RenderMhaStatusInfoBox({
  mhaStatus,
  showUnknownMhaStatus,
  overrideMhaStatus,
}: {
  mhaStatus:
    | {
        states: PatientStateWithContext[];
      }
    | undefined;
  showUnknownMhaStatus?: boolean;
  overrideMhaStatus?: MhaStatus;
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  // A forced status (e.g. the unmerge dialog previewing the "Incomplete" a
  // restored patient will be reset to) wins over the fetched status.
  if (overrideMhaStatus) {
    return (
      <MhaStatusInfoBox
        title="MHA Status"
        // Format via formatMhaStatus so the forced status reads exactly as it
        // will on the patient banner after the change (e.g. Incomplete shows as
        // "UNKNOWN (INCOMPLETE)", not a bare "INCOMPLETE").
        subtitle={formatMhaStatus(overrideMhaStatus).toUpperCase()}
        testId="mhaStatus"
      />
    );
  }

  if (!mhaStatus?.states?.[0] || showUnknownMhaStatus) {
    return (
      <MhaStatusInfoBox
        title="MHA Status"
        subtitle={MhaStatus.Unknown.toUpperCase()}
      />
    );
  }

  // Mirror the desktop dual-status detection (§6.5) so an active Section 136
  // running alongside a substantive detention is not hidden on mobile. The
  // compact info-box stays the mobile pattern — we surface the coexisting
  // status name rather than porting the full DualMhaStatusDisplay.
  const selection = selectSection136BannerStates(mhaStatus.states);

  if (selection.kind === "dual") {
    return (
      <Box
        data-testid="patient-banner-dual-mha-status-mobile"
        sx={{
          display: "flex",
          flexDirection: "column",
          border: `1px solid ${theme.palette.warning.main}`,
          borderRadius: "6px",
          overflow: "hidden",
        }}
      >
        <Banner
          bannerType={BannerList.WARNING}
          body={t("layout.patientBanner.dualMhaStatus.warning")}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "0.75em",
            padding: theme.spacing(2),
            backgroundColor: "white",
          }}
        >
          <MhaStatusInfoBox
            title={t("layout.patientBanner.dualMhaStatus.existingLabel")}
            subtitle={mhaStatusSubtitle(selection.other)}
            testId="mhaStatus"
          />
          <HorizontalLine noMargin={true} />
          <MhaStatusInfoBox
            title={t("layout.patientBanner.dualMhaStatus.coexistingLabel")}
            subtitle={mhaStatusSubtitle(selection.section136)}
            testId="coexistingMhaStatus"
          />
        </Box>
      </Box>
    );
  }

  return (
    <MhaStatusInfoBox
      title="MHA Status"
      subtitle={mhaStatusSubtitle(selection.state ?? mhaStatus.states[0])}
      testId="mhaStatus"
    />
  );
}

export {
  RenderDateOfBirth,
  RenderGpPractice,
  RenderMhaStatusInfoBox,
  RenderNhsNumber,
  RenderPatientAddress,
  RenderPatientAge,
  RenderPatientName,
  RenderPostCode,
};
